import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { PROJECT_THEME } from '../../theme'
import { collectMetadata } from './metadata'
import {
    Attachment,
    MAX_ATTACHMENT_BYTES,
    MAX_FILES,
    StoredUpload,
    UploadError,
    addAttachments,
    formatBytes,
    presign,
    putFile,
    refusesAttachments,
    totalBytes,
} from './attachments'

const ACCENT = PROJECT_THEME.colour.COLOUR_MID_LIGHT

const CONTACT_ENDPOINT = process.env.REACT_APP_CONTACT_ENDPOINT ?? '/api/contact'
const REQUEST_TIMEOUT_MS = 20000

const FACTS = [
    { label: 'Reply time', value: '< 1 day', accent: PROJECT_THEME.colour.COLOUR_LIGHT },
    { label: 'Discovery', value: '1–3 wks', accent: PROJECT_THEME.colour.COLOUR_MID_LIGHT },
    { label: 'NDA', value: 'On request', accent: PROJECT_THEME.colour.COLOUR_MID },
    { label: 'Engagement', value: 'Fixed or T&M', accent: PROJECT_THEME.colour.COLOUR_MID_DARK },
]

const Wrapper = styled.section`
    width: 100%;
    box-sizing: border-box;
    color: ${({ theme }) => theme.colour.OFF_WHITE};
    font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
`

const Eyebrow = styled.p`
    margin: 0 0 0.6em 0;
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-size: 0.7em;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: ${ACCENT};
`

const Heading = styled.h2`
    margin: 0 0 1em 0;
    font-family: ${({ theme }) => theme.font.FONT_SERIF_ROBOTO_SERIF};
    font-weight: 700;
    font-size: 1.4em;
    line-height: 1.2;
    letter-spacing: 1px;
`

const Subtitle = styled.p`
    margin: 0 0 1.2em 0;
    font-size: 0.95em;
    line-height: 1.55;
    max-width: 42em;
    color: ${({ theme }) => theme.colour.LIGHT_GREY};
`

const Fine = styled.p`
    margin: 1em 0 0 0;
    font-size: 0.75em;
    line-height: 1.5;
    color: ${({ theme }) => theme.colour.LIGHT_GREY};
`

const Frame = styled.div`
    border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
    border-radius: ${({ theme }) => theme.BORDER_RADIUS};
    overflow: hidden;
    display: flex;
    flex-direction: column;
`

const TitleBar = styled.div`
    display: flex;
    align-items: center;
    gap: 0.45em;
    padding: 0.6em 1em;
    border-bottom: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
`

const Dot = styled.span<{ colour: string }>`
    width: 0.55em;
    height: 0.55em;
    border-radius: 50%;
    background-color: ${({ colour }) => colour};
`

const BarTitle = styled.span`
    margin-left: 0.6em;
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-size: 0.7em;
    color: ${({ theme }) => theme.colour.LIGHT_GREY};
`

const Body = styled.div`
    padding: 1.5em;
`

const StatusBar = styled.div`
    display: flex;
    align-items: stretch;
    flex-wrap: wrap;
    border-top: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-size: 0.65em;
`

const StatusSegment = styled.span<{ colour: string; solid?: boolean }>`
    padding: 0.55em 0.9em;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${({ colour, solid, theme }) => (solid ? theme.colour.DARK_GREY : colour)};
    background-color: ${({ colour, solid }) => (solid ? colour : 'transparent')};
    border-right: 1px solid rgba(96, 96, 104, 0.5);

    &:last-child {
        border-right: 0;
        margin-left: auto;
    }
`

const FieldRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: 0.6em;
    width: 100%;
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    border-bottom: 1px dashed ${({ theme }) => theme.colour.LIGHT_GREY};
    padding: 0.55em 0;

    &:focus-within {
        border-bottom-color: ${ACCENT};
    }

    /* The prompt never wraps, so side-by-side leaves too little room for the
       input itself once the viewport is phone width. */
    @media (max-width: 40rem) {
        flex-direction: column;
        align-items: stretch;
        gap: 0.35em;
    }
`

const Prompt = styled.label`
    color: ${ACCENT};
    font-size: 0.82em;
    white-space: nowrap;
`

const fieldReset = css`
    flex: 1;
    min-width: 0;
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-size: 0.82em;
    color: ${({ theme }) => theme.colour.OFF_WHITE};
    background: transparent;
    border: 0;
    padding: 0;
    outline: none;

    &::placeholder {
        color: ${({ theme }) => theme.colour.LIGHT_GREY};
        opacity: 0.7;
    }

    /* Browsers paint an autofilled field in their own colours; the endless transition keeps the
       background from ever changing, and the fill colour keeps the text ours. */
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
        -webkit-text-fill-color: ${({ theme }) => theme.colour.OFF_WHITE};
        caret-color: ${({ theme }) => theme.colour.OFF_WHITE};
        transition: background-color 2147483647s ease-in-out 0s;
    }

    &:autofill {
        -webkit-text-fill-color: ${({ theme }) => theme.colour.OFF_WHITE};
        caret-color: ${({ theme }) => theme.colour.OFF_WHITE};
        filter: none;
    }

    /* Firefox adds a red glow to an invalid email once it has been edited. */
    &:-moz-ui-invalid {
        box-shadow: none;
    }

    &:user-invalid {
        box-shadow: none;
    }

    /* 16px keeps iOS from zooming the page when a field is focused, and the padding gives a tappable height. */
    @media (max-width: 40rem) {
        font-size: 16px;
        padding: 0.5em 0;
    }
`

const Input = styled.input`
    ${fieldReset};
`

const TextArea = styled.textarea`
    ${fieldReset};
    resize: vertical;
    min-height: 6em;
    line-height: 1.7;
`

const Button = styled.button<{ solid?: boolean }>`
    font-family: inherit;
    font-size: 0.82em;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    cursor: pointer;
    width: fit-content;
    padding: 0.8em 2.4em;
    border-radius: 2px;
    border: 1px solid ${ACCENT};
    background-color: ${({ solid }) => (solid ? ACCENT : 'transparent')};
    color: ${({ solid, theme }) => (solid ? theme.colour.DARK_GREY : ACCENT)};
    transition: background-color 160ms ease, color 160ms ease, transform 160ms ease;

    &:hover {
        background-color: ${({ solid }) => (solid ? 'transparent' : ACCENT)};
        color: ${({ solid, theme }) => (solid ? ACCENT : theme.colour.DARK_GREY)};
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.6;
        cursor: progress;
        transform: none;
    }
`

const AttachButton = styled.button`
    font-family: inherit;
    font-size: 0.72em;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    cursor: pointer;
    width: fit-content;
    padding: 0.55em 1.1em;
    border-radius: ${({ theme }) => theme.BORDER_RADIUS};
    border: 1px dashed ${({ theme }) => theme.colour.LIGHT_GREY};
    background: transparent;
    color: ${({ theme }) => theme.colour.LIGHT_GREY};
    transition: border-color 160ms ease, color 160ms ease;

    &:hover:not(:disabled) {
        border-color: ${ACCENT};
        color: ${ACCENT};
    }

    &:focus-visible {
        outline: 1px solid ${ACCENT};
        outline-offset: 2px;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`

const AttachBlock = styled.div`
    margin: 1.4em 0 1.2em 0;
`

const AttachRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.9em;
    flex-wrap: wrap;
`

const AttachSummary = styled.span`
    font-size: 0.75em;
    color: ${({ theme }) => theme.colour.LIGHT_GREY};
`

const AttachNotice = styled.p`
    margin: 0.7em 0 0 0;
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-size: 0.7em;
    line-height: 1.5;
    color: ${ACCENT};

    &:empty {
        margin: 0;
    }
`

const FileList = styled.ul`
    list-style: none;
    margin: 0.9em 0 0 0;
    padding: 0;
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-size: 0.75em;
`

const FileRow = styled.li`
    display: flex;
    align-items: center;
    gap: 0.9em;
    padding: 0.45em 0;
    border-bottom: 1px dashed ${({ theme }) => theme.colour.LIGHT_GREY};

    @media (max-width: 40rem) {
        flex-wrap: wrap;
        row-gap: 0.3em;
    }
`

const FileName = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (max-width: 40rem) {
        flex-basis: 100%;
    }
`

const FileMeta = styled.span<{ colour?: string }>`
    white-space: nowrap;
    color: ${({ colour, theme }) => colour ?? theme.colour.LIGHT_GREY};
`

const RemoveButton = styled(AttachButton)`
    font-size: 0.85em;
    padding: 0.3em 0.8em;

    @media (max-width: 40rem) {
        margin-left: auto;
        padding: 0.6em 1em;
    }
`

const HiddenFileInput = styled.input`
    display: none;
`

// Bots fill every field they can see in the DOM; humans never reach this one.
const HoneyPot = styled.div`
    position: absolute;
    left: -9999px;
    width: 1px;
    height: 1px;
    overflow: hidden;
`

type FieldProps = {
    name: string
    label: string
    value: string
    onChange: (value: string) => void
    required?: boolean
    type?: string
    rows?: number
    line: number
    onCaretMove: (element: HTMLInputElement | HTMLTextAreaElement, line: number) => void
}

function Field({
    name,
    label,
    value,
    onChange,
    required,
    type,
    rows,
    line,
    onCaretMove,
}: FieldProps) {
    const caretProps = {
        onSelect: (event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            onCaretMove(event.currentTarget, line),
        onFocus: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            onCaretMove(event.currentTarget, line),
    }

    return (
        <FieldRow>
            <Prompt htmlFor={name}>
                &gt; {label}
                {required ? '*' : ''}:
            </Prompt>
            {rows ? (
                <TextArea
                    id={name}
                    name={name}
                    rows={rows}
                    required={required}
                    value={value}
                    onChange={(event) => {
                        onChange(event.target.value)
                        onCaretMove(event.target, line)
                    }}
                    {...caretProps}
                />
            ) : (
                <Input
                    id={name}
                    name={name}
                    type={type ?? 'text'}
                    required={required}
                    value={value}
                    onChange={(event) => {
                        onChange(event.target.value)
                        onCaretMove(event.target, line)
                    }}
                    {...caretProps}
                />
            )}
        </FieldRow>
    )
}

type SendState = 'idle' | 'uploading' | 'sending' | 'sent' | 'error'

const STATUS_TEXT: Record<Exclude<SendState, 'idle' | 'uploading'>, string> = {
    sending: 'sending …',
    sent: 'sent — we will be in touch',
    error: 'send failed',
}

const KEPT = 'Your message and files are still here: try again, or email us directly.'

function percent(loaded: number, size: number) {
    return size > 0 ? Math.min(100, Math.floor((loaded / size) * 100)) : 100
}

function ProgressBar({ label, value }: { label: string; value: number }) {
    const filled = Math.round(value / 10)
    return (
        <FileMeta
            role="progressbar"
            aria-label={label}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={value}
            colour={ACCENT}
        >
            <span aria-hidden="true">[{'#'.repeat(filled) + '·'.repeat(10 - filled)}]</span> {value}%
        </FileMeta>
    )
}

const ORIGIN = { line: 1, column: 1 }

/* The form reads as a file: one line per field, and newlines inside the message
   push the line number further down. selectionStart is null on email and tel inputs. */
function caretPosition(element: HTMLInputElement | HTMLTextAreaElement, line: number) {
    if (element.selectionStart === null) return { line, column: element.value.length + 1 }

    const preceding = element.value.slice(0, element.selectionStart).split('\n')
    return {
        line: line + preceding.length - 1,
        column: preceding[preceding.length - 1].length + 1,
    }
}

export default function ContactForm() {
    const [name, setName] = useState('')
    const [company, setCompany] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [message, setMessage] = useState('')
    const [attachments, setAttachments] = useState<Attachment[]>([])
    const [progress, setProgress] = useState<Record<string, number>>({})
    const [attachNotice, setAttachNotice] = useState('')
    const [sendState, setSendState] = useState<SendState>('idle')
    const [errorMessage, setErrorMessage] = useState('')
    const [caret, setCaret] = useState(ORIGIN)

    const handleCaretMove = useCallback(
        (element: HTMLInputElement | HTMLTextAreaElement, line: number) =>
            setCaret(caretPosition(element, line)),
        []
    )

    const fileInputRef = useRef<HTMLInputElement>(null)
    const attachButtonRef = useRef<HTMLButtonElement>(null)
    const removeButtons = useRef(new Map<string, HTMLButtonElement>())
    const activeUploads = useRef(new Set<XMLHttpRequest>())

    useEffect(() => {
        const uploads = activeUploads.current
        return () => uploads.forEach((request) => request.abort())
    }, [])

    const busy = sendState === 'uploading' || sendState === 'sending'
    const attachedBytes = totalBytes(attachments)

    const handleFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
        const picked = Array.from(event.target.files ?? [])
        // Cleared so that picking a file again after removing it still fires a change.
        event.target.value = ''
        const added = addAttachments(attachments, picked)
        setAttachments(added.attachments)
        setAttachNotice(added.notice)
    }

    const removeAttachment = (id: string) => {
        const index = attachments.findIndex((attachment) => attachment.id === id)
        const remaining = attachments.filter((attachment) => attachment.id !== id)
        const neighbour = remaining[Math.min(index, remaining.length - 1)]

        setAttachments(remaining)
        setAttachNotice('')
        if (neighbour) removeButtons.current.get(neighbour.id)?.focus()
        else attachButtonRef.current?.focus()
    }

    const track = (request: XMLHttpRequest) => {
        activeUploads.current.add(request)
        return () => {
            activeUploads.current.delete(request)
        }
    }

    const upload = async (current: Attachment[]) => {
        const stored = new Map<string, StoredUpload>()
        current.forEach((attachment) => attachment.stored && stored.set(attachment.id, attachment.stored))

        const waiting = current.filter((attachment) => !attachment.stored)
        if (waiting.length > 0) {
            setSendState('uploading')
            setProgress({})
            const presigned = await presign(
                CONTACT_ENDPOINT,
                waiting.map((attachment) => attachment.file),
                REQUEST_TIMEOUT_MS
            )

            for (let index = 0; index < waiting.length; index += 1) {
                const { id, file } = waiting[index]
                const result = await putFile(
                    presigned[index],
                    file,
                    (loaded) => setProgress((previous) => ({ ...previous, [id]: loaded })),
                    track
                )
                stored.set(id, result)
                setAttachments((previous) =>
                    previous.map((attachment) => (attachment.id === id ? { ...attachment, stored: result } : attachment))
                )
            }
        }

        return current.map(({ id, file }) => {
            const { key, contentType } = stored.get(id) as StoredUpload
            return { key, fileName: file.name, contentType, sizeBytes: file.size }
        })
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (busy) return

        const payload = new FormData(event.currentTarget)

        if (payload.get('website')) {
            setSendState('sent')
            return
        }

        setErrorMessage('')
        setAttachNotice('')

        let uploaded
        try {
            uploaded = await upload(attachments)
        } catch (error) {
            setSendState('error')
            setErrorMessage(
                error instanceof UploadError && error.fileName
                    ? `Upload of ${error.fileName} failed. ${KEPT}`
                    : `Uploads are unavailable right now. ${KEPT}`
            )
            return
        }

        setSendState('sending')
        payload.delete('website')
        payload.append('metadata', JSON.stringify(collectMetadata()))
        payload.append('attachments', JSON.stringify(uploaded))

        const controller = new AbortController()
        const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

        try {
            // Content-Type is intentionally unset so the browser adds the multipart boundary.
            const response = await fetch(CONTACT_ENDPOINT, {
                method: 'POST',
                body: payload,
                signal: controller.signal,
            })

            if (response.status === 400 && (await refusesAttachments(response))) {
                setAttachments((previous) => previous.map(({ id, file }) => ({ id, file })))
            }
            if (!response.ok) throw new Error(`Request failed: ${response.status}`)

            setSendState('sent')
            setName('')
            setCompany('')
            setEmail('')
            setPhone('')
            setMessage('')
            setAttachments([])
            setProgress({})
            setCaret(ORIGIN)
        } catch (error) {
            setSendState('error')
            setErrorMessage('Something went wrong. Please email us directly.')
        } finally {
            window.clearTimeout(timeout)
        }
    }

    const uploadedBytes = attachments.reduce(
        (total, { id, file, stored }) => total + (stored ? file.size : Math.min(progress[id] ?? 0, file.size)),
        0
    )

    const fileStatus = ({ id, file, stored }: Attachment) => {
        if (stored) return <FileMeta colour={PROJECT_THEME.colour.COLOUR_LIGHT}>uploaded</FileMeta>
        if (sendState !== 'uploading') return null
        if (progress[id] === undefined) return <FileMeta>queued</FileMeta>
        return <ProgressBar label={`Uploading ${file.name}`} value={percent(progress[id], file.size)} />
    }

    return (
        <Wrapper>
            <Eyebrow>[ start a conversation ]</Eyebrow>
            <Heading>Want To Discuss A Project?</Heading>

            <Frame data-physics>
                <TitleBar>
                    <Dot colour={PROJECT_THEME.colour.COLOUR_MID_LIGHT} />
                    <Dot colour={PROJECT_THEME.colour.COLOUR_LIGHT} />
                    <Dot colour={PROJECT_THEME.colour.COLOUR_MID_DARK} />
                    <BarTitle>enquiry.txt — insert mode</BarTitle>
                </TitleBar>

                <Body>
                    <Subtitle>
                        Tell us what you are building. We reply within one business day with a
                        technical point of view, not a sales deck.
                    </Subtitle>

                    <form onSubmit={handleSubmit} encType="multipart/form-data">
                        <HoneyPot aria-hidden="true">
                            <input
                                type="text"
                                name="website"
                                tabIndex={-1}
                                autoComplete="off"
                                defaultValue=""
                            />
                        </HoneyPot>

                        <Field
                            name="name"
                            label="name"
                            required
                            line={1}
                            value={name}
                            onChange={setName}
                            onCaretMove={handleCaretMove}
                        />
                        <Field
                            name="company"
                            label="company"
                            line={2}
                            value={company}
                            onChange={setCompany}
                            onCaretMove={handleCaretMove}
                        />
                        <Field
                            name="email"
                            label="email"
                            type="email"
                            required
                            line={3}
                            value={email}
                            onChange={setEmail}
                            onCaretMove={handleCaretMove}
                        />
                        <Field
                            name="phone"
                            label="phone"
                            type="tel"
                            line={4}
                            value={phone}
                            onChange={setPhone}
                            onCaretMove={handleCaretMove}
                        />
                        <Field
                            name="message"
                            label="message"
                            required
                            rows={4}
                            line={5}
                            value={message}
                            onChange={setMessage}
                            onCaretMove={handleCaretMove}
                        />

                        <AttachBlock>
                            <AttachRow>
                                <AttachButton
                                    type="button"
                                    ref={attachButtonRef}
                                    disabled={busy}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    Attach files
                                </AttachButton>
                                <HiddenFileInput
                                    type="file"
                                    multiple
                                    accept=".png,.jpg,.jpeg,.webp,.pdf,.doc,.docx,.ppt,.pptx"
                                    ref={fileInputRef}
                                    onChange={handleFiles}
                                />
                                <AttachSummary>
                                    {attachments.length > 0
                                        ? `${attachments.length} of ${MAX_FILES} files · ` +
                                          `${formatBytes(attachedBytes)} of ${formatBytes(MAX_ATTACHMENT_BYTES)}`
                                        : `No files attached · up to ${MAX_FILES} files, ` +
                                          `${formatBytes(MAX_ATTACHMENT_BYTES)} in total`}
                                </AttachSummary>
                            </AttachRow>

                            {attachments.length > 0 && (
                                <FileList aria-label="Attached files">
                                    {attachments.map((attachment) => (
                                        <FileRow key={attachment.id}>
                                            <FileName title={attachment.file.name}>{attachment.file.name}</FileName>
                                            <FileMeta>{formatBytes(attachment.file.size)}</FileMeta>
                                            {fileStatus(attachment)}
                                            <RemoveButton
                                                type="button"
                                                aria-label={`Remove ${attachment.file.name}`}
                                                disabled={busy}
                                                onClick={() => removeAttachment(attachment.id)}
                                                ref={(element: HTMLButtonElement | null) => {
                                                    if (element) removeButtons.current.set(attachment.id, element)
                                                    else removeButtons.current.delete(attachment.id)
                                                }}
                                            >
                                                remove
                                            </RemoveButton>
                                        </FileRow>
                                    ))}
                                </FileList>
                            )}

                            <AttachNotice aria-live="polite">{attachNotice}</AttachNotice>
                        </AttachBlock>

                        <Button type="submit" solid disabled={busy}>
                            {sendState === 'uploading'
                                ? ':w uploading …'
                                : sendState === 'sending'
                                ? ':w sending …'
                                : ':w send'}
                        </Button>
                    </form>
                </Body>

                <StatusBar aria-live="polite">
                    {FACTS.map((fact, index) => (
                        <StatusSegment key={fact.label} colour={fact.accent} solid={index === 0}>
                            {fact.label}: {fact.value}
                        </StatusSegment>
                    ))}
                    <StatusSegment
                        colour={
                            sendState === 'error'
                                ? PROJECT_THEME.colour.COLOUR_MID_LIGHT
                                : sendState === 'sent'
                                ? PROJECT_THEME.colour.COLOUR_LIGHT
                                : PROJECT_THEME.colour.LIGHT_GREY
                        }
                    >
                        {errorMessage ||
                            (sendState === 'idle'
                                ? `utf-8 · ln ${caret.line}, col ${caret.column}`
                                : sendState === 'uploading'
                                ? `uploading … ${percent(uploadedBytes, attachedBytes)}%`
                                : STATUS_TEXT[sendState])}
                    </StatusSegment>
                </StatusBar>
            </Frame>

            <Fine>
                By submitting this form you agree for us to use the contact information provided to
                contact you and process your request. We also record your IP address and browser
                details with the enquiry to help us prevent abuse.
            </Fine>
        </Wrapper>
    )
}
